import { toast } from "react-toastify";

/**
 * Utility functions for optimized toast notifications on Windows
 */

// Default toast options optimized for Windows
const defaultToastOptions = {
  position: "top-right",
  autoClose: 6000, // Increased from 5000 for better visibility on Windows
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  pauseOnFocusLoss: false, // Important: Prevent toast from pausing when window loses focus
  theme: "light",
  style: {
    zIndex: 9999,
    opacity: 1,
    visibility: "visible",
    display: "block",
  },
  className: "toast-custom",
  bodyClassName: "toast-body-custom",
  progressClassName: "toast-progress-custom",
};

/**
 * Show success toast with Windows-optimized settings
 * @param {string} message - Toast message
 * @param {object} options - Additional toast options
 */
export const showSuccessToast = (message, options = {}) => {
  return toast.success(message, {
    ...defaultToastOptions,
    ...options,
  });
};

/**
 * Show error toast with Windows-optimized settings
 * @param {string} message - Toast message
 * @param {object} options - Additional toast options
 */
export const showErrorToast = (message, options = {}) => {
  return toast.error(message, {
    ...defaultToastOptions,
    autoClose: 8000, // Longer for error messages
    ...options,
  });
};

/**
 * Show warning toast with Windows-optimized settings
 * @param {string} message - Toast message
 * @param {object} options - Additional toast options
 */
export const showWarningToast = (message, options = {}) => {
  return toast.warning(message, {
    ...defaultToastOptions,
    autoClose: 7000,
    ...options,
  });
};

/**
 * Show info toast with Windows-optimized settings
 * @param {string} message - Toast message
 * @param {object} options - Additional toast options
 */
export const showInfoToast = (message, options = {}) => {
  return toast.info(message, {
    ...defaultToastOptions,
    ...options,
  });
};

/**
 * Get optimized ToastContainer props for Windows
 */
export const getOptimizedToastContainerProps = () => ({
  position: "top-right",
  autoClose: 6000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: false, // Critical for Windows compatibility
  pauseOnHover: true,
  draggable: true,
  theme: "light",
  style: { zIndex: 9999 },
  toastClassName: "toast-custom",
  bodyClassName: "toast-body-custom",
  progressClassName: "toast-progress-custom",
});

/**
 * Force refresh toast container (useful for Windows edge cases)
 */
export const forceRefreshToasts = () => {
  toast.dismiss();
  // Small delay to ensure proper cleanup
  setTimeout(() => {
    // Toast container will be ready for new toasts
  }, 100);
};

const toastUtils = {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showInfoToast,
  getOptimizedToastContainerProps,
  forceRefreshToasts,
};

export default toastUtils;
